import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Database, Download, PlayCircle, Loader2, Clock } from "lucide-react";
import { listBackups, runBackup, restoreBackup } from "../api";
import { PageHeader } from "./Dashboard";
import RefreshButton from "../components/RefreshButton";

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function Backups() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const reload = () => {
    setLoading(true);
    listBackups()
      .then(setBackups)
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await runBackup();
      toast.success(`Backup complete · ${res.total_documents} documents archived`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backup failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="admin-backups">
      <PageHeader
        eyebrow="Insurance"
        title="Backups"
        sub="Automated nightly snapshot · stored encrypted on Cloudinary · restorable from any point."
      />
      <div className="p-10">
        <div className="bg-[#161617] border border-[#2a2a2d] p-6 flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#c3ba9e]/15 flex items-center justify-center">
              <Database size={22} className="text-[#c3ba9e]" />
            </div>
            <div>
              <p className="text-[#e9e6db]">Daily auto-backup</p>
              <p className="text-[11px] text-[#8c8a7e] mt-1 flex items-center gap-1.5">
                <Clock size={11} /> Runs every day at 02:00 Asia/Kolkata
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <RefreshButton onClick={reload} loading={loading} testid="admin-backups-refresh" />
            <button
              onClick={runNow}
              disabled={running}
              className="bg-[#c3ba9e] text-[#0d0d0e] px-5 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#FDFAF6] disabled:opacity-60 flex items-center gap-2"
              data-testid="admin-backup-run"
            >
              {running ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />}
              {running ? "Backing up…" : "Back up now"}
            </button>
          </div>
        </div>

        <div className="bg-[#161617] border border-[#2a2a2d]">
          <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr_0.6fr] gap-4 px-6 py-3 border-b border-[#2a2a2d] text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">
            <span>Snapshot</span>
            <span>Created</span>
            <span>Documents</span>
            <span>Size</span>
            <span className="text-right">Download</span>
          </div>
          {loading ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">Loading…</p>
          ) : backups.length === 0 ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">
              No backups yet. Click "Back up now" to create the first one.
            </p>
          ) : (
            backups.map((b) => (
              <div
                key={b.public_id}
                className="grid grid-cols-[1.4fr_1fr_1fr_1fr_0.6fr] gap-4 px-6 py-4 border-b border-[#2a2a2d] items-center hover:bg-[#1a1a1c] transition-colors"
                data-testid={`backup-${b.public_id}`}
              >
                <span className="text-[#e9e6db] text-sm truncate">{b.public_id}</span>
                <span className="text-[#9a9787] text-[12px]">{fmtDate(b.created_at)}</span>
                <span className="text-[#9a9787] text-sm">{b.total_documents}</span>
                <span className="text-[#9a9787] text-sm">{fmtBytes(b.bytes)}</span>
                <a
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-right text-[11px] tracking-[0.22em] uppercase text-[#c3ba9e] hover:underline flex items-center justify-end gap-1.5"
                >
                  <Download size={12} /> .gz
                </a>
              </div>
            ))
          )}
        </div>

        {backups.length > 0 && <RestorePanel backups={backups} onDone={reload} />}

        <details className="mt-8 text-[12px] text-[#8c8a7e]">
          <summary className="cursor-pointer hover:text-[#c3ba9e]">How restore works</summary>
          <div className="mt-3 leading-relaxed">
            Pick any snapshot above and click <span className="text-[#c3ba9e]">Restore</span>. Restore replaces these collections only:
            products, blog_posts, site_content, categories. Admin accounts are never touched.
          </div>
        </details>
      </div>
    </div>
  );
}

function RestorePanel({ backups, onDone }) {
  const [selected, setSelected] = useState(backups[0]?.url || "");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!selected) return;
    if (!window.confirm("Restore will OVERWRITE current products, blogs, site content and categories. Continue?")) return;
    setBusy(true);
    try {
      const res = await restoreBackup(selected);
      toast.success(`Restored: ${Object.entries(res.restored).map(([k, v]) => `${k}=${v}`).join(", ")}`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-8 bg-[#161617] border border-[#2a2a2d] p-6">
      <p className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">Restore from snapshot</p>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="bg-[#0d0d0e] border border-[#2a2a2d] text-[#e9e6db] px-3 py-2.5 text-[13px] min-w-[260px]" data-testid="admin-restore-select">
          {backups.map((b) => {
            const label = `${new Date(b.created_at).toLocaleString("en-IN")} · ${b.total_documents} docs`;
            return (
              <option key={b.public_id} value={b.url}>{label}</option>
            );
          })}
        </select>
        <button
          onClick={run}
          disabled={busy || !selected}
          className="bg-[#c97070] text-[#0d0d0e] px-5 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#e69a9a] disabled:opacity-60"
          data-testid="admin-restore-run"
        >
          {busy ? "Restoring…" : "Restore"}
        </button>
      </div>
    </div>
  );
}
